import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import css from './SignatureServices.module.css';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Icons from '../../../../../components/Icons/Icons';
import InfoPopup from '../../../../../components/InfoPopup/InfoPopup';
import '../../../../../styles/react-tooltip.css';
import { sortServicesTypesAlphabetically } from '../../../../../util/genericHelpers';
import { TALENT_SERVICES_GUIDES } from '../../../../../util/enums';
import GuidePopup from '../../../../../components/GuidePopup/GuidePopup';

const endorsementOptions = [
  {
    platform: 'Facebook',
    guideLink: TALENT_SERVICES_GUIDES.FACEBOOK,
    types: ['Post', 'Story', '30-Minute Live Q&A'],
  },
  {
    platform: 'Instagram',
    guideLink: TALENT_SERVICES_GUIDES.INSTAGRAM,
    types: ['Post', 'Reels', 'Story', 'Carousel Post', '30-Minute Live Q&A'],
  },
  {
    platform: 'Snapchat',
    guideLink: TALENT_SERVICES_GUIDES.SNAPCHAT,
    types: ['Spotlight', 'Story'],
  },
  {
    platform: 'YouTube',
    guideLink: TALENT_SERVICES_GUIDES.YOUTUBE,
    types: ['Post', 'Shorts', '30-Minute Live Q&A'],
  },
  {
    platform: 'TikTok',
    guideLink: TALENT_SERVICES_GUIDES.TIKTOK,
    types: ['Post', 'Live Mention', '30-Minute Live Q&A'],
  },
  {
    platform: 'LinkedIn',
    guideLink: TALENT_SERVICES_GUIDES.LINKEDIN,
    types: ['Post', 'Article', 'Recommendation'],
  },
  {
    platform: 'Twitter',
    guideLink: TALENT_SERVICES_GUIDES.TWITTER,
    types: ['Post', 'Video', 'Spaces Feature', 'Pinned Tweet', 'Poll', 'Giveaway Collaboration'],
  },
  {
    platform: 'Pinterest',
    guideLink: TALENT_SERVICES_GUIDES.PINTEREST_PIN,
    types: ['Pinterest Pin'],
  },
  {
    platform: 'Meta',
    guideLink: TALENT_SERVICES_GUIDES.META_THREADS_POST,
    types: ['Threads Post'],
  },
];

const tooltipMap = {
  // Facebook
  'Facebook-Post': 'Create a dedicated Facebook post to showcase a brand or message.',
  'Facebook-Story': 'Feature brands or messages in a temporary yet impactful Facebook Story.',
  'Facebook-30-Minute Live Q&A':
    'A real-time 30-minute session to answer questions and connect with your audience',

  // Instagram
  'Instagram-Post': 'Share brands or messages in high-visibility Instagram posts.',
  'Instagram-Reels':
    'Engage audiences with a creative and dynamic Instagram Reel featuring your brand or message.',
  'Instagram-Story': 'Create short-lived but impactful Instagram Stories for brands.',
  'Instagram-Carousel Post':
    'Feature multiple images in a single Instagram post for maximum impact.',
  'Instagram-30-Minute Live Q&A': 'Mention or showcase brands during your Instagram Live sessions.',

  // Snapchat
  'Snapchat-Spotlight': 'Create featured Snapchat Spotlights to help brands reach wider audiences.',
  'Snapchat-Story': 'Feature brands or messages in engaging Snapchat Stories.',

  // YouTube
  'YouTube-Post': 'Create dedicated YouTube posts featuring brands or messages.',
  'YouTube-Shorts': 'Make quick, engaging promotional videos for posting on YouTube Shorts.',
  'YouTube-30-Minute Live Q&A':
    'Offer a real-time 30-minute session to answer questions and connect with your audience',

  // TikTok
  'TikTok-Post': 'Create custom TikTok videos to engage your audience with brands or messages.',
  'TikTok-Live Mention': 'Showcase products or brands during your TikTok Live sessions.',
  'TikTok-30-Minute Live Q&A':
    'Offer a real-time 30-minute session to answer questions and connect with your audience',

  // LinkedIn
  'LinkedIn-Post': 'Share professional LinkedIn posts to highlight brands or achievements.',
  'LinkedIn-Article': 'Publish professional articles discussing brands or industries.',
  'LinkedIn-Recommendation': 'Provide professional endorsements on LinkedIn.',

  // Twitter
  'Twitter-Post': 'Create dedicated tweets featuring brands or messages.',
  'Twitter-Video': 'Make custom video tweets to maximize engagement and exposure.',
  'Twitter-Spaces Feature': 'Mention brands or join discussions in Twitter Spaces sessions.',
  'Twitter-Pinned Tweet': 'Pin brands or messages to the top of your profile for maximum exposure.',
  'Twitter-Poll': 'Create polls featuring brands or products for engagement.',
  'Twitter-Giveaway Collaboration': 'Host promotional giveaways with your Twitter audience.',

  // Pinterest & Threads
  'Pinterest-Pinterest Pin': 'Feature products or services in dedicated Pinterest posts.',
  'Meta-Threads Post': "Create dedicated posts on Meta's Threads platform for brand exposure",
};

const Endorsements = ({ values }) => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeSubIndex, setActiveSubIndex] = useState(null);

  const handleAccordionClick = index => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const handleSubAccordionClick = index => {
    setActiveSubIndex(index === activeSubIndex ? null : index);
  };

  const faqsData = [
    {
      question: intl.formatMessage({
        id: 'Endorsements.socialMediaTitle',
        defaultMessage: 'Social Media',
      }),
      answer: (
        <FieldArray name="endorsements">
          {({ fields }) => {
            const endorsements = fields.value || [];

            return (
              <div className={css.socialMediaSec}>
                {endorsementOptions.map((endorsement, platformIndex) => (
                  <div
                    key={endorsement.platform}
                    className={classNames(css.faqCard, {
                      [css.activeFAQ]: activeSubIndex === platformIndex,
                    })}
                  >
                    <div
                      className={css.faqBody}
                      onClick={() => handleSubAccordionClick(platformIndex)}
                    >
                      <span className={css.faqHeading} role="button">
                        <div className={css.guideLink}>
                          {endorsement.platform}
                          <GuidePopup externalLink={endorsement?.guideLink} />
                        </div>
                        <span className={css.faqIcon}>
                          {activeSubIndex === platformIndex ? (
                            <Icons name="upAngle" />
                          ) : (
                            <Icons name="downAngle" />
                          )}
                        </span>
                      </span>

                      {activeSubIndex === platformIndex && (
                        <div className={css.faqDescription} onClick={e => e.stopPropagation()}>
                          <div className={css.endorsementSec}>
                            {sortServicesTypesAlphabetically(endorsement.types).map(
                              (type, typeIndex) => {
                                const endorsementKey = `${endorsement.platform}-${type}`;
                                const currentEndorsement = endorsements.find(
                                  item =>
                                    item.platform === endorsement.platform && item.type === type
                                );

                                return (
                                  <div className={css.formFld} key={endorsementKey}>
                                    <label className={css.talentOptions}>
                                      <input
                                        type="checkbox"
                                        checked={!!currentEndorsement}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            fields.push({
                                              platform: endorsement.platform,
                                              type,
                                              selected: true,
                                              price: '',
                                            });
                                          } else {
                                            const indexToRemove = endorsements.findIndex(
                                              item =>
                                                item.platform === endorsement.platform &&
                                                item.type === type
                                            );
                                            if (indexToRemove !== -1) {
                                              fields.remove(indexToRemove);
                                            }
                                          }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                      />
                                      <span>
                                        {type}
                                        {tooltipMap[endorsementKey] && (
                                          <InfoPopup
                                            triggerOn="hover"
                                            info={tooltipMap[endorsementKey]}
                                          />
                                        )}
                                      </span>
                                    </label>

                                    {currentEndorsement && (
                                      <div className={css.customFldSec}>
                                        <span className={css.currency}>$</span>
                                        <Field
                                          name={`endorsements.${endorsements.indexOf(
                                            currentEndorsement
                                          )}.price`}
                                          className={css.input}
                                          component="input"
                                          type="number"
                                          placeholder={`Enter flat price for ${type}`}
                                          onClick={e => e.stopPropagation()}
                                          parse={value => {
                                            const parsedValue = parseFloat(value);
                                            return isNaN(parsedValue) ? 0 : parsedValue;
                                          }}
                                        />
                                        <span className={css.price}>.00</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
        </FieldArray>
      ),
    },
  ];

  return (
    <>
      {faqsData.map((faq, index) => (
        <div
          key={index}
          className={classNames(css.faqCard, {
            [css.activeFAQ]: activeIndex === index,
          })}
          onClick={() => handleAccordionClick(index)}
        >
          <div className={css.faqBody}>
            <span className={css.faqHeading}>
              <div>{faq?.question} </div>
              <span className={css.faqIcon}>
                {activeIndex === index ? <Icons name="upAngle" /> : <Icons name="downAngle" />}
              </span>
            </span>
            {activeIndex === index && (
              <div className={css.faqDescription} onClick={e => e.stopPropagation()}>
                {faq.answer}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default Endorsements;
